import { useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { usePageLoader } from '@/components/LogoLoader'
import { fmtDate, fmtNumber, intlLocale, useT } from '@/i18n'
import { crewsMessages } from '@/i18n/messages/crews'
import { Badge } from './Badge'
import type { CrewAttendance, CrewDay } from './types'

/**
 * Attendance from AU's check-ins: a half-year calendar, how stable the crew's make-up is, and
 * how the check-ins are recorded. AU has no real clock-in times, so "discipline" is about the
 * record itself — same-day, closed, and by whom — not about punctuality.
 */
export function AttendanceTab({ crewId }: { crewId: string }) {
  const t = useT(crewsMessages)
  const { data, isPending, error } = useQuery<CrewAttendance>({
    queryKey: ['crew-attendance', crewId],
    queryFn: () => api.get<CrewAttendance>(`/api/crews/${crewId}/attendance`),
    staleTime: 5 * 60_000,
  })
  const loader = usePageLoader(isPending, t('loadingAttendance'))
  if (loader) return loader
  if (error || !data) return <p className="text-sm" style={{ color: 'var(--viz-critical)' }}>{t('notFound')}</p>

  const worked = data.days.filter((d) => d.workers > 0).length
  const off = data.days.filter((d) => d.workers === 0 && d.notWorking > 0).length
  const weekdayGaps = data.days.filter((d) => d.workers === 0 && d.notWorking === 0 && isWeekday(d.day) && !isFuture(d.day)).length
  const disc = data.discipline
  const pct = (n: number, of: number) => (of > 0 ? `${fmtNumber((n / of) * 100, 0)}%` : '—')

  return (
    <div className="space-y-6">
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">{t('calendarTitle')}</h2>
        <p className="text-xs text-neutral-500">
          {t('calendarSummary', { worked, off, gaps: weekdayGaps })}
        </p>
        <Calendar days={data.days} />
      </section>

      <div className="grid gap-3 lg:grid-cols-2">
        <Panel title={t('stabilityTitle')} hint={t('stabilityHint')}>
          <Months months={data.months} />
          <h4 className="mt-4 text-xs font-medium text-neutral-500">{t('tenureTitle')}</h4>
          <ul className="mt-1 divide-y divide-neutral-100 text-sm dark:divide-neutral-800">
            {data.tenure.map((p) => (
              <li key={p.employeeId} className="flex items-baseline justify-between gap-3 py-1.5">
                <span className="truncate">{p.name ?? '—'}</span>
                <span className="tabular shrink-0 text-xs text-neutral-500">
                  {t('tenureSince', { date: fmtDate(p.firstDay) })} · {t('tenureDays', { count: p.days })}
                </span>
              </li>
            ))}
            {data.tenure.length === 0 && <li className="py-1.5 text-neutral-500">{t('noMembers', { days: 90 })}</li>}
          </ul>
        </Panel>

        <Panel title={t('disciplineTitle')} hint={t('disciplineHint')}>
          <dl className="grid grid-cols-2 gap-2">
            <Fig label={t('discSameDay')} value={pct(disc.sameDay, disc.entries)} good={disc.sameDay >= disc.entries * 0.95} />
            <Fig label={t('discMorning')} value={pct(disc.morning, disc.entries)} />
            <Fig label={t('discLater')} value={fmtNumber(disc.later)} warn={disc.later > disc.entries * 0.1} />
            <Fig label={t('discUnclosed')} value={pct(disc.unclosed, disc.workEntries)} warn={disc.unclosed > disc.workEntries * 0.1} hint={t('discUnclosedHint')} />
            <Fig label={t('discLeaderMissing')} value={`${disc.leaderMissingDays} / ${disc.crewDays}`} warn={disc.leaderMissingDays > disc.crewDays * 0.1} />
            <Fig label={t('discEntries')} value={fmtNumber(disc.entries)} />
          </dl>
          <h4 className="mt-4 text-xs font-medium text-neutral-500">{t('recordedByTitle')}</h4>
          <ul className="mt-1 space-y-1 text-sm">
            {disc.recordedBy.map((r) => (
              <li key={r.name} className="flex items-center justify-between gap-2">
                <span className="flex min-w-0 items-center gap-1.5">
                  <span className="truncate">{r.name}</span>
                  {r.isBauleiter && <Badge tone="brand">Bauleiter</Badge>}
                  {r.isLeader && <Badge tone="green">{t('badgeLeader')}</Badge>}
                </span>
                <span className="tabular shrink-0 text-xs text-neutral-500">{pct(r.entries, disc.entries)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>
    </div>
  )
}

const isWeekday = (iso: string) => { const d = new Date(`${iso}T12:00:00Z`).getUTCDay(); return d >= 1 && d <= 5 }
const isFuture = (iso: string) => iso > new Date().toISOString().slice(0, 10)

/** Weeks as columns, Monday on top — a GitHub-style year view, half a year wide. */
function Calendar({ days }: { days: CrewDay[] }) {
  const t = useT(crewsMessages)
  const [hover, setHover] = useState<CrewDay | null>(null)
  const weeks: CrewDay[][] = []
  for (let i = 0; i < days.length; i += 7) weeks.push(days.slice(i, i + 7))
  const maxMetres = Math.max(...days.map((d) => d.metres), 1)
  const month = new Intl.DateTimeFormat(intlLocale(), { month: 'short' })

  const cell = (d: CrewDay) => {
    if (isFuture(d.day)) return 'bg-transparent'
    if (d.workers > 0) return ''
    if (d.notWorking > 0) return 'bg-amber-200 dark:bg-amber-500/40'
    return 'bg-neutral-100 dark:bg-neutral-800'
  }
  const green = (d: CrewDay) =>
    d.workers > 0 ? { backgroundColor: `color-mix(in srgb, var(--viz-good) ${35 + Math.round((d.metres / maxMetres) * 65)}%, transparent)` } : undefined

  return (
    <div className="rounded-2xl bg-white p-3 ring-1 ring-neutral-200 sm:p-4 dark:bg-neutral-900 dark:ring-neutral-800">
      <div className="overflow-x-auto">
        <div className="inline-flex gap-[3px] lg:gap-1" onMouseLeave={() => setHover(null)}>
          {weeks.map((w) => (
            <div key={w[0]!.day} className="flex flex-col gap-[3px] lg:gap-1">
              <span className="h-3 text-[9px] leading-3 text-neutral-400">
                {Number(w[0]!.day.slice(8)) <= 7 ? month.format(new Date(`${w[0]!.day}T12:00:00Z`)) : ''}
              </span>
              {w.map((d) => (
                <span
                  key={d.day}
                  onMouseEnter={() => setHover(d)}
                  title={`${fmtDate(d.day)}`}
                  className={`h-3.5 w-3.5 rounded-[3px] sm:h-4 sm:w-4 lg:h-[22px] lg:w-[22px] ${cell(d)}`}
                  style={green(d)}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-2 flex min-h-5 flex-wrap items-center justify-between gap-2 text-xs text-neutral-500">
        <span className="tabular">
          {hover ? (
            <>
              <strong className="font-medium text-neutral-800 dark:text-neutral-200">{fmtDate(hover.day)}</strong>{' · '}
              {hover.workers > 0
                ? t('calWorked', { count: hover.workers, metres: fmtNumber(hover.metres) })
                : hover.notWorking > 0 ? t('calNotWorking', { count: hover.notWorking }) : t('calNothing')}
            </>
          ) : t('calHover')}
        </span>
        <span className="flex items-center gap-3">
          <Legend swatch={<span className="inline-block h-3 w-3 rounded-[3px]" style={{ backgroundColor: 'var(--viz-good)' }} />}>{t('calLegendWorked')}</Legend>
          <Legend swatch={<span className="inline-block h-3 w-3 rounded-[3px] bg-amber-200 dark:bg-amber-500/40" />}>{t('calLegendOff')}</Legend>
          <Legend swatch={<span className="inline-block h-3 w-3 rounded-[3px] bg-neutral-100 dark:bg-neutral-800" />}>{t('calLegendNone')}</Legend>
        </span>
      </div>
    </div>
  )
}

function Legend({ swatch, children }: { swatch: ReactNode; children: ReactNode }) {
  return <span className="inline-flex items-center gap-1">{swatch}{children}</span>
}

function Months({ months }: { months: CrewAttendance['months'] }) {
  const t = useT(crewsMessages)
  const max = Math.max(...months.map((m) => m.members), 1)
  const fmt = new Intl.DateTimeFormat(intlLocale(), { month: 'short' })
  return (
    <div className="flex h-36 items-end gap-1">
      {months.map((m) => (
        <div key={`${m.year}-${m.month}`} className="flex min-w-0 flex-1 flex-col items-center justify-end gap-0.5"
          title={t('monthTooltip', { members: m.members, inn: m.cameIn, out: m.droppedOut, days: m.workerDays })}>
          <span className="tabular text-[10px] leading-3 text-green-600 dark:text-green-400">{m.cameIn > 0 ? `+${m.cameIn}` : ''}</span>
          <span className="tabular text-[10px] leading-3 text-amber-600 dark:text-amber-400">{m.droppedOut > 0 ? `−${m.droppedOut}` : ''}</span>
          <div className="w-full max-w-[22px] rounded-t-[4px]"
            style={{ height: `${Math.round((m.members / max) * 80)}px`, minHeight: m.members ? 4 : 0, backgroundColor: 'var(--viz-series-1)' }} />
          <span className="tabular text-[10px] text-neutral-700 dark:text-neutral-300">{m.members}</span>
          <span className="truncate text-[9px] text-neutral-400">{fmt.format(new Date(Date.UTC(m.year, m.month - 1, 15)))}</span>
        </div>
      ))}
    </div>
  )
}

function Panel({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="rounded-2xl bg-white p-4 ring-1 ring-neutral-200 dark:bg-neutral-900 dark:ring-neutral-800">
      <h3 className="text-sm font-semibold">{title}</h3>
      {hint && <p className="mt-0.5 text-xs text-neutral-500">{hint}</p>}
      <div className="mt-3">{children}</div>
    </section>
  )
}

function Fig({ label, value, hint, warn, good }: { label: string; value: string; hint?: string; warn?: boolean; good?: boolean }) {
  return (
    <div className="rounded-xl bg-neutral-50 p-3 dark:bg-neutral-800/50" title={hint}>
      <dd className={`tabular text-xl font-semibold ${warn ? 'text-amber-600 dark:text-amber-400' : good ? 'text-green-600 dark:text-green-400' : ''}`}>{value}</dd>
      <dt className="text-[11px] leading-tight text-neutral-500">{label}</dt>
    </div>
  )
}
